import { toast } from "./toast";

const anonymousThemeStorageKey = "stackora-theme";
const authenticatedThemeStoragePrefix = "stackora-theme:";
const themeSyncStoragePrefix = "stackora-theme-sync:";
const defaultAccentAnonymousStorageKey = "stackora-accent-schema";
const defaultAccentAuthenticatedStoragePrefix = "stackora-accent-schema:";
const defaultAccentAllowed = Object.freeze(["green", "purple"]);
const darkMediaQuery = "(prefers-color-scheme: dark)";

let accentStorageListenerBound = false;

function normalizeTheme(theme) {
    return theme === "dark" || theme === "light"
        ? theme
        : null;
}

function getThemeConfig() {
    const config = window.__stackoraThemeConfig || {};
    const syncTtlMs = Number(config.syncTtlMs);
    const accentAnon = typeof config.accentAnonymousStorageKey === "string" && config.accentAnonymousStorageKey.length > 0
        ? config.accentAnonymousStorageKey
        : defaultAccentAnonymousStorageKey;
    const accentPrefix = typeof config.accentAuthenticatedStoragePrefix === "string" && config.accentAuthenticatedStoragePrefix.length > 0
        ? config.accentAuthenticatedStoragePrefix
        : defaultAccentAuthenticatedStoragePrefix;
    const accentAllowed = Array.isArray(config.accentAllowed) && config.accentAllowed.length > 0
        ? config.accentAllowed
        : defaultAccentAllowed;
    const defaultAccentSchema = typeof config.defaultAccentSchema === "string" && config.defaultAccentSchema.length > 0
        ? config.defaultAccentSchema
        : null;

    return {
        forcedTheme: normalizeTheme(config.forcedTheme),
        initialTheme: normalizeTheme(config.initialTheme),
        syncUrl: typeof config.syncUrl === "string" && config.syncUrl.length > 0
            ? config.syncUrl
            : "",
        syncTtlMs: Number.isFinite(syncTtlMs) && syncTtlMs > 0
            ? syncTtlMs
            : 60000,
        userId: typeof config.userId === "string" && config.userId.length > 0
            ? config.userId
            : "",
        accentAnonymousStorageKey: accentAnon,
        accentAuthenticatedStoragePrefix: accentPrefix,
        accentAllowed: accentAllowed,
        defaultAccentSchema: defaultAccentSchema
    };
}

function getThemeStorageKey(config) {
    return config.userId
        ? authenticatedThemeStoragePrefix + config.userId
        : anonymousThemeStorageKey;
}

function getThemeSyncStorageKey(config) {
    return config.userId
        ? themeSyncStoragePrefix + config.userId
        : "";
}

function getAccentSchemaStorageKey(config) {
    return config.userId
        ? config.accentAuthenticatedStoragePrefix + config.userId
        : config.accentAnonymousStorageKey;
}

function normalizeAccentSchema(schema, config) {
    const cfg = config || getThemeConfig();
    if (schema === null || schema === undefined) {
        return null;
    }
    const s = String(schema).trim().toLowerCase();
    return cfg.accentAllowed.indexOf(s) >= 0
        ? s
        : null;
}

function getStoredAccentSchema(config) {
    return normalizeAccentSchema(readLocalStorage(getAccentSchemaStorageKey(config)), config);
}

function persistAccentSchema(schema, config) {
    const key = getAccentSchemaStorageKey(config);
    const normalized = normalizeAccentSchema(schema, config);
    if (normalized) {
        writeLocalStorage(key, normalized);
    } else {
        removeLocalStorage(key);
    }
}

function applyFaviconForAccent(schema) {
    try {
        const svgLink = document.querySelector('link[rel="icon"][type="image/svg+xml"]');
        if (!svgLink || !svgLink.hasAttribute("data-accent-favicon")) {
            return;
        }
        const pngLinks = document.querySelectorAll('link[rel="icon"][type="image/png"], link[rel="apple-touch-icon"]');
        const suffix = schema === "purple" ? "-purple" : "";
        // Strip any existing accent suffix then re-apply the correct one
        const svgBase = svgLink.href.replace(/-purple\.svg(\?.*)?$/, ".svg").replace(/\.svg(\?.*)?$/, "");
        svgLink.href = svgBase + suffix + ".svg";
        pngLinks.forEach(function (link) {
            const pngBase = link.href.replace(/-purple-180\.png(\?.*)?$/, "-180.png").replace(/-180\.png(\?.*)?$/, "");
            link.href = pngBase + suffix + "-180.png";
        });
    } catch {
        // Non-critical — favicon swap is a best-effort enhancement
    }
}

function applyAccentSchema(schema) {
    const cfg = getThemeConfig();
    const root = document.documentElement;
    const normalized = normalizeAccentSchema(schema, cfg);
    if (normalized) {
        root.setAttribute("data-accent-schema", normalized);
    } else {
        root.removeAttribute("data-accent-schema");
    }
    applyFaviconForAccent(normalized);
}

function syncAccentSchemaRadios() {
    const cfg = getThemeConfig();
    const current = normalizeAccentSchema(document.documentElement.getAttribute("data-accent-schema"), cfg);
    // When no user override is stored, fall back to the tenant's default so the UI shows a selection
    const effective = current || normalizeAccentSchema(cfg.defaultAccentSchema, cfg);
    document.querySelectorAll("[data-accent-schema-form]").forEach(function (form) {
        form.querySelectorAll("[data-accent-schema-input]").forEach(function (input) {
            if (!(input instanceof HTMLInputElement)) {
                return;
            }
            input.checked = Boolean(effective) && input.value === effective;
        });
    });
}

function readLocalStorage(key) {
    if (!key) {
        return null;
    }

    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeLocalStorage(key, value) {
    if (!key) {
        return;
    }

    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Ignore storage failures and keep the in-memory theme applied.
    }
}

function removeLocalStorage(key) {
    if (!key) {
        return;
    }

    try {
        window.localStorage.removeItem(key);
    } catch {
        // Ignore storage failures and keep the in-memory theme applied.
    }
}

function getStoredTheme(config) {
    const storageKey = getThemeStorageKey(config);
    return normalizeTheme(readLocalStorage(storageKey));
}

function getSystemTheme() {
    return window.matchMedia && window.matchMedia(darkMediaQuery).matches
        ? "dark"
        : "light";
}

function getPreferredTheme(config) {
    if (config.forcedTheme) {
        return config.forcedTheme;
    }

    if (config.initialTheme) {
        return config.initialTheme;
    }

    const storedTheme = getStoredTheme(config);
    if (storedTheme) {
        return storedTheme;
    }

    return getSystemTheme();
}

function applyTheme(theme) {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.setAttribute("data-bs-theme", theme);
    root.style.colorScheme = theme;

    const isDark = theme === "dark";
    const actionLabel = isDark ? "Turn dark mode off" : "Turn dark mode on";

    document.querySelectorAll("[data-theme-toggle]").forEach(function (button) {
        button.setAttribute("aria-pressed", String(isDark));
        button.setAttribute("aria-label", actionLabel);
        button.setAttribute("title", actionLabel);

        const state = button.querySelector("[data-theme-toggle-state]");
        if (state) {
            state.textContent = isDark ? "On" : "Off";
        }
    });
}

function persistTheme(theme, config) {
    writeLocalStorage(getThemeStorageKey(config), theme);
}

function clearStoredTheme(config) {
    removeLocalStorage(getThemeStorageKey(config));
}

function markThemeSynced(config) {
    const syncKey = getThemeSyncStorageKey(config);
    if (!syncKey) {
        return;
    }

    writeLocalStorage(syncKey, String(Date.now()));
}

function shouldSyncTheme(config) {
    if (config.forcedTheme || !config.userId || !config.syncUrl) {
        return false;
    }

    const syncKey = getThemeSyncStorageKey(config);
    const lastSynced = Number(readLocalStorage(syncKey));
    return !Number.isFinite(lastSynced) || (Date.now() - lastSynced) >= config.syncTtlMs;
}

function getThemeStatusElement(form) {
    return form
        ? form.querySelector("[data-theme-status]")
        : null;
}

function setThemeStatus(form, message, isError) {
    const status = getThemeStatusElement(form);
    if (!status) {
        return;
    }

    if (!message) {
        status.hidden = true;
        status.textContent = "";
        status.classList.remove("settings-status-error");
        status.removeAttribute("role");
        return;
    }

    status.hidden = false;
    status.textContent = message;
    status.classList.toggle("settings-status-error", Boolean(isError));
    if (isError) {
        status.setAttribute("role", "alert");
    } else {
        status.removeAttribute("role");
    }
}

function setThemeToggleSaving(button, isSaving) {
    button.dataset.themeSaving = isSaving ? "true" : "false";
    button.disabled = isSaving;
    button.setAttribute("aria-busy", isSaving ? "true" : "false");
}

async function postThemePreference(theme, form, config) {
    const formData = new FormData();
    const csrfToken = form.querySelector('input[name="__RequestVerificationToken"]');
    if (csrfToken && csrfToken.value) {
        formData.append("__RequestVerificationToken", csrfToken.value);
    }

    formData.append("theme", theme);

    const response = await fetch(config.syncUrl, {
        method: "POST",
        body: formData,
        credentials: "same-origin",
        headers: {
            "Accept": "application/json"
        }
    });

    let payload = null;
    try {
        payload = await response.json();
    } catch {
        payload = null;
    }

    if (!response.ok || !payload || payload.success !== true) {
        throw new Error(payload && payload.message ? payload.message : "Theme save failed.");
    }

    return payload;
}

let themeSyncInFlight = false;

async function syncThemeFromServer(config) {
    if (!shouldSyncTheme(config) || themeSyncInFlight) {
        return;
    }

    themeSyncInFlight = true;
    try {
        const response = await fetch(config.syncUrl, {
            method: "GET",
            credentials: "same-origin",
            headers: {
                "Accept": "application/json"
            }
        });

        if (!response.ok) {
            return;
        }

        const payload = await response.json();
        const serverTheme = normalizeTheme(payload && payload.theme);
        window.__stackoraThemeConfig = window.__stackoraThemeConfig || {};
        window.__stackoraThemeConfig.initialTheme = serverTheme;

        // Only a real server value is worth applying. Null means this account has never saved a preference —
        // not that the user asked for the OS setting — so clearing the local choice on null would discard a
        // real selection and snap the page to the system theme mid-session.
        if (serverTheme) {
            persistTheme(serverTheme, config);
            applyTheme(serverTheme);
        }

        markThemeSynced(config);
    } catch {
        // Ignore sync failures and keep the cached theme applied.
    } finally {
        themeSyncInFlight = false;
    }
}

function bindThemeToggles(config) {
    document.querySelectorAll("[data-theme-toggle]").forEach(function (button) {
        if (button.dataset.themeBound === "true") {
            return;
        }

        button.dataset.themeBound = "true";
        button.addEventListener("click", function () {
            if (config.forcedTheme || button.dataset.themeSaving === "true") {
                return;
            }

            const currentTheme = normalizeTheme(document.documentElement.getAttribute("data-theme")) || getPreferredTheme(config);
            const nextTheme = currentTheme === "dark" ? "light" : "dark";
            const previousStoredTheme = getStoredTheme(config);
            const form = button.closest("[data-theme-preference-form]");

            persistTheme(nextTheme, config);
            applyTheme(nextTheme);

            if (!form || !config.syncUrl) {
                markThemeSynced(config);
                return;
            }

            setThemeStatus(form, "Saving appearance…", false);
            setThemeToggleSaving(button, true);

            postThemePreference(nextTheme, form, config)
                .then(function (payload) {
                    const savedTheme = normalizeTheme(payload.theme) || nextTheme;
                    window.__stackoraThemeConfig = window.__stackoraThemeConfig || {};
                    window.__stackoraThemeConfig.initialTheme = savedTheme;
                    persistTheme(savedTheme, config);
                    applyTheme(savedTheme);
                    markThemeSynced(config);
                    setThemeStatus(form, payload.message || "Appearance saved.", false);
                    toast.success(payload.message || "Appearance saved.");
                })
                .catch(function (error) {
                    window.__stackoraThemeConfig = window.__stackoraThemeConfig || {};
                    window.__stackoraThemeConfig.initialTheme = normalizeTheme(window.__stackoraThemeConfig.initialTheme);
                    if (previousStoredTheme) {
                        persistTheme(previousStoredTheme, config);
                    } else {
                        clearStoredTheme(config);
                    }
                    applyTheme(currentTheme);
                    var themeErrMsg = error && error.message ? error.message : "We couldn't save your appearance right now. Please try again.";
                    setThemeStatus(form, themeErrMsg, true);
                    toast.error(themeErrMsg);
                })
                .finally(function () {
                    setThemeToggleSaving(button, false);
                });
        });
    });
}

function watchSystemTheme(config) {
    if (!window.matchMedia || config.forcedTheme) {
        return;
    }

    const mediaQuery = window.matchMedia(darkMediaQuery);
    const handleChange = function () {
        if (config.initialTheme || getStoredTheme(config)) {
            return;
        }

        applyTheme(getSystemTheme());
    };

    if (typeof mediaQuery.addEventListener === "function") {
        mediaQuery.addEventListener("change", handleChange);
        return;
    }

    if (typeof mediaQuery.addListener === "function") {
        mediaQuery.addListener(handleChange);
    }
}

function bindAccentSchemaControls(config) {
    document.querySelectorAll("[data-accent-schema-form]").forEach(function (form) {
        if (form.dataset.accentSchemaBound === "true") {
            return;
        }
        form.dataset.accentSchemaBound = "true";

        form.querySelectorAll("[data-accent-schema-input]").forEach(function (input) {
            input.addEventListener("change", function () {
                if (!(input instanceof HTMLInputElement) || !input.checked) {
                    return;
                }
                const next = normalizeAccentSchema(input.value, config);
                if (!next) {
                    return;
                }
                persistAccentSchema(next, config);
                applyAccentSchema(next);
                syncAccentSchemaRadios();
            });
        });

        const reset = form.querySelector("[data-accent-schema-reset]");
        if (reset) {
            reset.addEventListener("click", function () {
                persistAccentSchema(null, config);
                applyAccentSchema(null);
                syncAccentSchemaRadios();
            });
        }
    });
}

function bindAccentSchemaStorageSync() {
    if (accentStorageListenerBound) {
        return;
    }
    accentStorageListenerBound = true;
    window.addEventListener("storage", function (e) {
        if (!e.key) {
            return;
        }
        const cfg = getThemeConfig();
        if (e.key !== getAccentSchemaStorageKey(cfg)) {
            return;
        }
        const next = normalizeAccentSchema(e.newValue, cfg);
        applyAccentSchema(next);
        syncAccentSchemaRadios();
    });
}

function initAccentSchemaUi(config) {
    const stored = getStoredAccentSchema(config);
    if (stored) {
        applyAccentSchema(stored);
    } else {
        applyAccentSchema(null);
    }
    syncAccentSchemaRadios();
    bindAccentSchemaControls(config);
    bindAccentSchemaStorageSync();
}

function initThemeUi() {
    const config = getThemeConfig();
    const initialTheme = getPreferredTheme(config);

    if (config.initialTheme) {
        persistTheme(config.initialTheme, config);
    }

    applyTheme(initialTheme);
    initAccentSchemaUi(config);
    bindThemeToggles(config);
    watchSystemTheme(config);
    syncThemeFromServer(config);
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initThemeUi);
} else {
    initThemeUi();
}
